import { ExternalLink, Menu, X } from 'lucide-react';
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
} from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { Button, cn } from '@workspace/shadcn';

import { useModals } from './modals.tsx';

const ABOUT_URL = 'https://www.kfshi.com.sa/en-sa/AboutUs';
const PORTAL_URL = import.meta.env.VITE_USER_APP_URL ?? '/';

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'Sign in', to: `${PORTAL_URL}/signin`, external: true },
  { label: 'About', to: ABOUT_URL, external: true, offsite: true },
];

function NavLink({
  to,
  label,
  offsite,
  onClick,
  className,
  external,
}: {
  to: string;
  label: string;
  offsite?: boolean;
  onClick?: () => void;
  className?: string;
  external?: boolean;
}) {
  const location = useLocation();
  const isActive = !external && location.pathname === to;

  const face = offsite ? (
    <span className="flex items-center gap-1">
      {label}
      <ExternalLink className="size-3 shrink-0" aria-hidden />
    </span>
  ) : (
    label
  );

  const content = (
    <>
      <span className="inline-block transition-transform duration-300 will-change-transform group-hover:-translate-y-full">
        {face}
      </span>
      <span className="text-primary absolute inset-0 translate-y-full transition-transform duration-300 will-change-transform group-hover:translate-y-0">
        {face}
      </span>
    </>
  );

  const classes = cn(
    'group relative overflow-hidden font-medium whitespace-nowrap transition-colors',
    isActive ? 'text-foreground' : 'text-muted-foreground',
    className,
  );

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noreferrer"
        onClick={onClick}
        className={classes}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={to} onClick={onClick} className={classes}>
      {content}
    </Link>
  );
}

function AnimatedButton({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-primary text-background group relative inline-block overflow-hidden rounded-md px-6 py-2 text-base font-medium transition-all">
      <span className="ease-in-out-cubic absolute bottom-0 left-0 h-48 w-full origin-bottom translate-y-full transform overflow-hidden rounded-full bg-white/15 transition-all duration-300 group-hover:translate-y-14" />
      <span className="whitespace-nowrap">{children}</span>
    </span>
  );
}

function BrandLogo({ className }: { className?: string }) {
  return (
    <img
      className={cn('h-auto', className)}
      src="/images/logo-lockup.webp"
      alt="ITGL Academy"
      width={520}
      height={450}
      loading="eager"
    />
  );
}

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { openModal } = useModals();
  const { scrollY } = useScroll();
  const reduced = useReducedMotion();

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setIsScrolled(latest > 20);
  });

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const closeMenu = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <motion.header
        initial={reduced ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className={cn(
          'fixed inset-x-0 top-0 border-b transition-colors duration-300',
          openModal ? 'z-10' : 'z-30',
          isScrolled && !isOpen
            ? 'border-border bg-background/80 backdrop-blur-md'
            : 'border-transparent bg-transparent',
        )}
      >
        <div className="mx-auto w-full max-w-8xl px-4 lg:px-6">
          <div className="flex h-20 items-center justify-between gap-4 md:h-24 lg:h-28">
            <Link to="/" className="shrink-0" aria-label="ITGL Academy home">
              <BrandLogo className="w-20 md:w-24 lg:w-28" />
            </Link>

            <div className="flex items-center gap-2">
              <nav className="hidden items-center gap-6 md:flex">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    label={link.label}
                    external={link.external}
                    offsite={link.offsite}
                  />
                ))}
                <Link to="/courses" className="w-fit shrink-0">
                  <AnimatedButton>Our Courses</AnimatedButton>
                </Link>
              </nav>

              <Button
                aria-label="Toggle navigation"
                aria-expanded={isOpen}
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(!isOpen)}
                className="text-muted-foreground shrink-0 px-2 md:hidden"
              >
                {isOpen ? (
                  <X className="size-5" />
                ) : (
                  <Menu className="size-5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: reduced ? 0.15 : 0.4, ease: [0.42, 0, 0.58, 1] }}
            className="bg-background fixed inset-0 z-20 flex flex-col items-center justify-center"
          >
            <Link to="/" className="mb-10" onClick={closeMenu}>
              <BrandLogo className="w-32" />
            </Link>
            <nav className="flex flex-col items-center space-y-8">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  label={link.label}
                  external={link.external}
                  offsite={link.offsite}
                  onClick={closeMenu}
                  className="text-2xl"
                />
              ))}
              <Link to="/courses" onClick={closeMenu}>
                <Button className="h-12 px-8 text-base text-white dark:text-black">
                  Our Courses
                </Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
